import { isEscape, returnFocus, trapFocus } from "./utils.js";

const mqNav = window.matchMedia("(min-width: 992px)");

let activeTrapRelease = null;

function releasePrimaryNavTrap() {
  if (activeTrapRelease) {
    activeTrapRelease();
    activeTrapRelease = null;
  }
}

function syncMenuToggleAria(openBtn) {
  const labelOpen =
    openBtn.getAttribute("data-label-open") || "Open navigation";
  const labelClose =
    openBtn.getAttribute("data-label-close") || "Close navigation";
  if (mqNav.matches) {
    openBtn.setAttribute("aria-label", labelOpen);
    return;
  }
  const open = document.documentElement.classList.contains(
    "site-header--nav-open",
  );
  openBtn.setAttribute("aria-label", open ? labelClose : labelOpen);
}

function setPrimaryNavOpen(open) {
  document.documentElement.classList.toggle("site-header--nav-open", open);
  syncPrimaryNavA11y();
}

function syncPrimaryNavA11y() {
  const nav = document.getElementById("primary-navigation");
  const openBtn = document.getElementById("site-navigation-open");
  if (!nav || !openBtn) {
    return;
  }

  if (mqNav.matches) {
    nav.removeAttribute("inert");
    nav.removeAttribute("aria-hidden");
    openBtn.setAttribute("aria-expanded", "false");
    syncMenuToggleAria(openBtn);
    return;
  }

  const open = document.documentElement.classList.contains(
    "site-header--nav-open",
  );
  openBtn.setAttribute("aria-expanded", open ? "true" : "false");
  syncMenuToggleAria(openBtn);
  if (open) {
    nav.removeAttribute("inert");
    nav.setAttribute("aria-hidden", "false");
  } else {
    nav.setAttribute("inert", "");
    nav.setAttribute("aria-hidden", "true");
  }
}

function getFocusable(root) {
  if (!root) {
    return [];
  }
  const sel =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll(sel)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

function getHeaderSearchEls() {
  const cluster = document.querySelector(".site-header__cluster");
  const panel = document.getElementById("site-header-search-panel");
  const toggle = document.getElementById("site-header-search-toggle");
  const input = document.getElementById("site-header-search-input");
  if (!cluster || !panel || !toggle) {
    return null;
  }
  return { cluster, panel, toggle, input };
}

function getSearchCluster() {
  return document.querySelector(".site-header__cluster");
}

function initHeaderNav() {
  const openBtn = document.getElementById("site-navigation-open");
  const nav = document.getElementById("primary-navigation");
  if (!openBtn || !nav) {
    return;
  }

  openBtn.addEventListener("click", () => {
    if (document.documentElement.classList.contains("site-header--nav-open")) {
      closeMobilePrimaryNavOverlay();
    } else {
      window.returnFocusTo = openBtn;
      closeHeaderSearch();
      closePrimaryNavDropdowns();
      setPrimaryNavOpen(true);
      const first = getFocusable(nav)[0];
      if (first) {
        first.focus();
      }
    }
  });

  const scrim = document.getElementById("site-header-nav-scrim");
  if (scrim) {
    scrim.addEventListener("click", () => {
      closeMobilePrimaryNavOverlay();
    });
  }

  syncPrimaryNavA11y();
}

function syncSearchPanelForBreakpoint() {
  const els = getHeaderSearchEls();
  if (!els) {
    return;
  }
  const { cluster, panel } = els;
  if (mqNav.matches) {
    const open = cluster.classList.contains(
      "site-header__cluster--search-open",
    );
    if (open) {
      panel.removeAttribute("hidden");
    } else {
      panel.setAttribute("hidden", "");
    }
  } else {
    panel.removeAttribute("hidden");
  }
}

function closeHeaderSearch() {
  const els = getHeaderSearchEls();
  if (!els) {
    return;
  }
  const { cluster, panel, toggle } = els;
  cluster.classList.remove("site-header__cluster--search-open");
  if (mqNav.matches) {
    panel.setAttribute("hidden", "");
  } else {
    panel.removeAttribute("hidden");
  }
  toggle.setAttribute("aria-expanded", "false");
}

function openHeaderSearch() {
  const els = getHeaderSearchEls();
  if (!els) {
    return;
  }
  const { cluster, panel, toggle, input } = els;

  closePrimaryNavDropdowns();
  window.returnFocusTo = toggle;

  if (!mqNav.matches) {
    setPrimaryNavOpen(true);
  } else {
    cluster.classList.add("site-header__cluster--search-open");
    panel.removeAttribute("hidden");
    toggle.setAttribute("aria-expanded", "true");
  }

  requestAnimationFrame(() => {
    input?.focus();
  });
}

function initHeaderSearch() {
  const els = getHeaderSearchEls();
  if (!els) {
    return;
  }
  const { toggle, cluster } = els;
  const dismiss = document.getElementById("site-header-search-dismiss");

  toggle.addEventListener("click", () => {
    const open = cluster.classList.contains(
      "site-header__cluster--search-open",
    );
    if (open) {
      closeHeaderSearch();
      returnFocus();
    } else {
      openHeaderSearch();
    }
  });

  if (dismiss) {
    dismiss.addEventListener("click", () => {
      closeHeaderSearch();
      returnFocus();
    });
  }

  syncSearchPanelForBreakpoint();
}

function onSiteHeaderMatchMediaChange() {
  setPrimaryNavOpen(false);
  closeHeaderSearch();
  closePrimaryNavDropdowns();
  if (!mqNav.matches) {
    syncSearchPanelForBreakpoint();
  }
  syncHeaderDropdownGeometry();
  if (mqNav.matches) {
    syncOpenDesktopDropdownPosition();
  }
}

function syncActiveNavUnderlineOffset(mainRect) {
  const labels = document.querySelectorAll(".site-header__nav-trigger-label");
  if (!labels.length) {
    return;
  }

  labels.forEach((label) => {
    if (!(label instanceof HTMLElement)) {
      return;
    }

    const labelRect = label.getBoundingClientRect();
    const rawOffset = mainRect.bottom - labelRect.bottom;
    const offset = Math.max(0, Math.round(rawOffset));
    label.style.setProperty("--site-header-active-line-offset", `${-offset}px`);
  });
}

function syncHeaderDropdownGeometry() {
  const header = document.getElementById("site-header");
  const main = document.querySelector(".site-header__main");
  if (!header || !main) {
    return;
  }
  const headerRect = header.getBoundingClientRect();
  const mainRect = main.getBoundingClientRect();
  const right = Math.max(0, window.innerWidth - mainRect.right);
  document.documentElement.style.setProperty(
    "--site-header-dropdown-top",
    `${Math.round(headerRect.bottom)}px`,
  );
  document.documentElement.style.setProperty(
    "--site-header-dropdown-right",
    `${Math.round(right)}px`,
  );
  document.documentElement.style.setProperty(
    "--site-header-main-inner-width",
    `${Math.round(mainRect.width)}px`,
  );

  if (!mqNav.matches) {
    return;
  }

  syncActiveNavUnderlineOffset(mainRect);
}

function clearDesktopDropdownPanelLayout(panel) {
  panel.style.left = "";
  panel.style.right = "";
  panel.style.maxWidth = "";
  panel.style.transformOrigin = "";
}

function scheduleDropdownLayoutClear(panel) {
  if (!mqNav.matches) {
    clearDesktopDropdownPanelLayout(panel);
    return;
  }

  let settled = false;

  const doClear = () => {
    if (settled) return;
    settled = true;
    panel.removeEventListener("transitionend", onEnd);
    // Guard: don't clear if the panel was re-opened before the animation finished.
    if (panel.getAttribute("aria-hidden") !== "false") {
      clearDesktopDropdownPanelLayout(panel);
    }
  };

  const onEnd = (e) => {
    if (e.propertyName === "transform") doClear();
  };

  panel.addEventListener("transitionend", onEnd);
  // Fallback in case transitionend never fires (e.g. display:none, detach).
  setTimeout(doClear, 800);
}

function syncOpenDesktopDropdownPosition() {
  if (!mqNav.matches) {
    return;
  }
  const trigger = document.querySelector(
    '.site-header__nav-trigger[aria-expanded="true"]',
  );
  if (!trigger) {
    return;
  }
  const panelId = trigger.getAttribute("aria-controls");
  const panel = panelId ? document.getElementById(panelId) : null;
  const main = document.querySelector(".site-header__main");
  if (!panel || !main || panel.getAttribute("aria-hidden") === "true") {
    return;
  }

  const mainRect = main.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();
  let width = panel.getBoundingClientRect().width;
  if (width < 1) {
    width = panel.offsetWidth;
  }

  const leftCandidate = triggerRect.left;
  const wouldPassMainRight = leftCandidate + width > mainRect.right - 1;

  if (wouldPassMainRight) {
    const rightPx = Math.max(0, window.innerWidth - mainRect.right);
    panel.style.left = "auto";
    panel.style.right = `${Math.round(rightPx)}px`;
    panel.style.transformOrigin = "top right";
    panel.style.maxWidth = `${Math.round(mainRect.width)}px`;
  } else {
    panel.style.right = "auto";
    panel.style.left = `${Math.round(leftCandidate)}px`;
    panel.style.transformOrigin = "top left";
    const available = Math.max(0, mainRect.right - leftCandidate);
    panel.style.maxWidth = `${Math.round(available)}px`;
  }
}

function closePrimaryNavDropdowns() {
  releasePrimaryNavTrap();
  document.querySelectorAll(".site-header__nav-trigger").forEach((btn) => {
    btn.setAttribute("aria-expanded", "false");
  });
  document.querySelectorAll(".site-header__dropdown").forEach((panel) => {
    panel.setAttribute("aria-hidden", "true");
    scheduleDropdownLayoutClear(panel);
    panel.setAttribute("inert", "");
  });
}

function closeMobilePrimaryNavOverlay() {
  closePrimaryNavDropdowns();
  setPrimaryNavOpen(false);
  returnFocus();
}

function applyDropdownOpen(panel, trigger, isOpen) {
  if (!panel || !trigger) {
    return;
  }
  trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
  if (isOpen) {
    panel.setAttribute("aria-hidden", "false");
    panel.removeAttribute("inert");
  } else {
    panel.setAttribute("aria-hidden", "true");
    scheduleDropdownLayoutClear(panel);
    panel.setAttribute("inert", "");
  }
}

function closeDropdownAndFocusTrigger(panel, trigger) {
  if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) {
    return;
  }
  releasePrimaryNavTrap();
  applyDropdownOpen(panel, trigger, false);
  trigger.focus();
}

function initPrimaryMegaNav() {
  const nav = document.getElementById("primary-navigation");
  if (!nav) {
    return;
  }

  const triggers = nav.querySelectorAll(".site-header__nav-trigger");
  if (!triggers.length) {
    return;
  }

  function syncDropdownPositionIfDesktop() {
    if (mqNav.matches) {
      syncHeaderDropdownGeometry();
      syncOpenDesktopDropdownPosition();
    }
  }

  function onLayoutBreakpointChange() {
    syncHeaderDropdownGeometry();
    if (!mqNav.matches) {
      closePrimaryNavDropdowns();
    } else {
      syncOpenDesktopDropdownPosition();
    }
  }

  syncHeaderDropdownGeometry();
  window.addEventListener("resize", onLayoutBreakpointChange, {
    passive: true,
  });
  window.addEventListener("scroll", syncDropdownPositionIfDesktop, {
    passive: true,
  });

  nav.addEventListener("click", (e) => {
    if (!(e.target instanceof Element)) {
      return;
    }
    const back = e.target.closest(".site-header__dropdown-back");
    if (!back || !nav.contains(back)) {
      return;
    }
    e.preventDefault();
    const panel = back.closest(".site-header__dropdown");
    if (!(panel instanceof HTMLElement)) {
      return;
    }
    const panelId = panel.getAttribute("id");
    const trigger = panelId
      ? document.querySelector(`[aria-controls="${panelId}"]`)
      : null;
    if (trigger instanceof HTMLElement) {
      closeDropdownAndFocusTrigger(panel, trigger);
    }
  });

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      const keyboardTriggered = e.detail === 0;
      const expanded = trigger.getAttribute("aria-expanded") === "true";
      const panelId = trigger.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) {
        return;
      }
      if (expanded) {
        applyDropdownOpen(panel, trigger, false);
      } else {
        closePrimaryNavDropdowns();
        if (mqNav.matches) {
          syncHeaderDropdownGeometry();
        }
        applyDropdownOpen(panel, trigger, true);
        if (mqNav.matches) {
          window.requestAnimationFrame(() => {
            syncHeaderDropdownGeometry();
            syncOpenDesktopDropdownPosition();
            window.requestAnimationFrame(() => {
              syncOpenDesktopDropdownPosition();
              if (keyboardTriggered) {
                activeTrapRelease = trapFocus(panel, {
                  focusFirst: true,
                  focusDelay: 0,
                  onEscape: () => closeDropdownAndFocusTrigger(panel, trigger),
                });
              }
            });
          });
        }
      }
    });
  });

  document.addEventListener(
    "click",
    (e) => {
      if (!mqNav.matches) {
        return;
      }
      const t = e.target;
      if (!(t instanceof Node)) {
        return;
      }
      if (nav.contains(t)) {
        return;
      }
      closePrimaryNavDropdowns();
    },
    true,
  );
}

function initGlobalEscape() {
  document.body.addEventListener("keyup", (e) => {
    if (!isEscape(e)) {
      return;
    }
    if (
      !mqNav.matches &&
      document.documentElement.classList.contains("site-header--nav-open")
    ) {
      const openTrigger = document.querySelector(
        '#primary-navigation .site-header__nav-trigger[aria-expanded="true"]',
      );
      if (openTrigger instanceof HTMLElement) {
        const panelId = openTrigger.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel instanceof HTMLElement) {
          closeDropdownAndFocusTrigger(panel, openTrigger);
          return;
        }
      }
    }
    closePrimaryNavDropdowns();
    const cluster = getSearchCluster();
    if (
      mqNav.matches &&
      cluster?.classList.contains("site-header__cluster--search-open")
    ) {
      closeHeaderSearch();
    }
    if (document.documentElement.classList.contains("site-header--nav-open")) {
      setPrimaryNavOpen(false);
    }
    returnFocus();
  });
}

function initHeaderStickyState() {
  const header = document.getElementById("site-header");
  if (!header) {
    return;
  }
  const scrolledClass = "site-header--scrolled";
  const body = document.body;
  const thresholdPx = 8;
  let ticking = false;

  function update() {
    const y = window.scrollY || document.documentElement.scrollTop;
    body.classList.toggle(scrolledClass, y > thresholdPx);
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  update();
}

initHeaderNav();
initHeaderSearch();
initPrimaryMegaNav();
mqNav.addEventListener("change", onSiteHeaderMatchMediaChange);
initGlobalEscape();
initHeaderStickyState();
